<script setup lang="ts" generic="T extends Record<string, unknown>">
import { computed } from "vue";
import { ChevronDown, ChevronUp } from "lucide-vue-next";

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  width?: string;
  align?: "left" | "right";
  /** Optional accessor that returns the sort key for a row. */
  sortValue?: (row: T) => string | number | undefined;
}

const props = defineProps<{
  columns: Column<T>[];
  rows: T[];
  sortKey?: string | null;
  sortDir?: "asc" | "desc";
  rowKey: (row: T) => string;
  empty?: string;
  loading?: boolean;
  /** Optional row click handler. Rows are presented as role="link" when set. */
  onRowClick?: (row: T) => void;
}>();

const emit = defineEmits<{
  (e: "sort", key: string): void;
  (e: "row-keydown", row: T, ev: KeyboardEvent): void;
}>();

const sortedRows = computed(() => {
  if (!props.sortKey) return props.rows;
  const col = props.columns.find((c) => c.key === props.sortKey);
  if (!col) return props.rows;
  const dir = props.sortDir === "desc" ? -1 : 1;
  return [...props.rows].sort((a, b) => {
    const av = col.sortValue ? col.sortValue(a) : (a as Record<string, unknown>)[col.key];
    const bv = col.sortValue ? col.sortValue(b) : (b as Record<string, unknown>)[col.key];
    if (av == null && bv == null) return 0;
    if (av == null) return -dir;
    if (bv == null) return dir;
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
});

function onHeaderClick(c: Column<T>) {
  if (!c.sortable) return;
  emit("sort", c.key);
}
</script>

<template>
  <div class="overflow-x-auto">
    <table class="w-full border-collapse text-[13px]">
      <thead>
        <tr class="border-b border-border text-left text-2xs uppercase tracking-wide text-fg-muted dark:border-border-dark">
          <th
            v-for="c in columns"
            :key="c.key"
            scope="col"
            class="px-3 py-2 font-medium"
            :class="{
              'cursor-pointer select-none hover:text-fg dark:hover:text-fg-dark': c.sortable,
              'text-right': c.align === 'right',
            }"
            :style="c.width ? { width: c.width } : undefined"
            @click="onHeaderClick(c)"
          >
            <span class="inline-flex items-center gap-1">
              {{ c.header }}
              <template v-if="c.sortable && sortKey === c.key">
                <ChevronUp v-if="sortDir === 'asc'" :size="12" />
                <ChevronDown v-else :size="12" />
              </template>
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-if="loading">
          <tr v-for="i in 6" :key="`s-${i}`" class="skeleton-row border-b border-border dark:border-border-dark">
            <td v-for="c in columns" :key="c.key" class="px-3 py-2">
              <span class="block h-3 w-3/4 rounded bg-surface dark:bg-bg-dark" />
            </td>
          </tr>
        </template>
        <template v-else>
          <tr
            v-for="row in sortedRows"
            :key="rowKey(row)"
            class="h-[36px] border-b border-border text-fg hover:bg-surface dark:border-border-dark dark:text-fg-dark dark:hover:bg-surface-dark"
            :class="{ 'cursor-pointer': !!onRowClick }"
            :role="onRowClick ? 'link' : undefined"
            :tabindex="onRowClick ? 0 : undefined"
            @click="onRowClick?.(row)"
            @keydown="(ev: KeyboardEvent) => { if (ev.key === 'Enter' && onRowClick) onRowClick(row); emit('row-keydown', row, ev); }"
          >
            <td
              v-for="c in columns"
              :key="c.key"
              class="px-3 py-2 align-middle"
              :class="{ 'text-right': c.align === 'right' }"
            >
              <slot :name="`cell-${c.key}`" :row="row">
                {{ (row as Record<string, unknown>)[c.key] }}
              </slot>
            </td>
          </tr>
          <tr v-if="!sortedRows.length">
            <td :colspan="columns.length" class="px-3 py-8 text-center text-fg-muted">
              {{ empty ?? "No rows." }}
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
